import { useState, useEffect } from 'react';
import { publicApi } from '../api/client';
import {
  FALLBACK_PILLARS,
  FALLBACK_ROADMAP_PREVIEW,
  FALLBACK_SOCIALS,
  FALLBACK_HERO,
  FALLBACK_ABOUT_HEADER,
  FALLBACK_ABOUT_STORY,
  FALLBACK_ROADMAP_HEADER,
  FALLBACK_COMMUNITY_HEADER,
  FALLBACK_ROADMAP_FULL,
} from '../data/fallbackContent';

function sectionByKey(sections, key) {
  return sections.find((s) => s.section_key === key);
}

function buildContent(data) {
  const sections = data?.sections || [];
  const hero = sectionByKey(sections, 'hero') || FALLBACK_HERO;
  const aboutHeader = sectionByKey(sections, 'about_header') || FALLBACK_ABOUT_HEADER;
  const aboutStory = sectionByKey(sections, 'about_story');
  const storyBody = aboutStory?.body || FALLBACK_ABOUT_STORY;
  const storyExtra = aboutStory?.extra_data || {};
  const roadmapHeader = sectionByKey(sections, 'roadmap_header') || FALLBACK_ROADMAP_HEADER;
  const communityHeader = sectionByKey(sections, 'community_header') || FALLBACK_COMMUNITY_HEADER;
  const roadmapPageHeader = sectionByKey(sections, 'roadmap_page_header') || FALLBACK_ROADMAP_HEADER;

  const pillars = sections
    .filter((s) => s.section_type === 'pillar')
    .sort((a, b) => a.sort_order - b.sort_order)
    .map((s) => ({
      icon: s.extra_data?.icon || s.extra_data?.emoji,
      title: s.title,
      text: s.lead,
    }));

  const roadmapPreview = (data?.roadmapPreview || []).map((r) => ({
    phase: r.phase,
    title: r.title,
    desc: r.description,
  }));

  const roadmapFull = (data?.roadmapItems || []).length ? data.roadmapItems : FALLBACK_ROADMAP_FULL;

  return {
    announcements: Array.isArray(data?.announcements) ? data.announcements : [],
    pillars: pillars.length ? pillars : FALLBACK_PILLARS,
    roadmapPreview: roadmapPreview.length ? roadmapPreview : FALLBACK_ROADMAP_PREVIEW,
    roadmapFull,
    socials: data?.socials?.length ? data.socials : FALLBACK_SOCIALS,
    hero: {
      eyebrow: hero.eyebrow || FALLBACK_HERO.eyebrow,
      title: hero.title || FALLBACK_HERO.title,
      lead: hero.lead || FALLBACK_HERO.lead,
    },
    aboutHeader: {
      eyebrow: aboutHeader.eyebrow,
      title: aboutHeader.title,
      lead: aboutHeader.lead,
    },
    aboutStory: {
      ...FALLBACK_ABOUT_STORY,
      ...storyBody,
      evolution_image: storyExtra.evolution_image || FALLBACK_ABOUT_STORY.evolution_image,
    },
    roadmapHeader: {
      eyebrow: roadmapHeader.eyebrow,
      title: roadmapHeader.title,
      lead: roadmapHeader.lead,
    },
    communityHeader: {
      eyebrow: communityHeader.eyebrow,
      title: communityHeader.title,
      lead: communityHeader.lead,
    },
    roadmapPageHeader: {
      eyebrow: roadmapPageHeader.eyebrow,
      title: roadmapPageHeader.title,
      lead: roadmapPageHeader.lead,
    },
  };
}

export function useContent() {
  const [content, setContent] = useState(() => buildContent(null));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    publicApi
      .getContent()
      .then((data) => {
        if (!cancelled) setContent(buildContent(data));
      })
      .catch(() => {
        if (!cancelled) setContent(buildContent(null));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, []);

  return { content, loading };
}
